﻿import Icon from '../../../components/AppIcon';
import {
  formatTime,
  formatHoursMinutes,
  calculateWorkingHours,
  getCurrentDateTime,
  formatTimeHHMMSS,
} from '../../../utils/timeTracker';

const TodayLogsWidget = ({ todayLogs, summary }) => {
  return (
    <div className="bg-surface border border-border rounded-lg p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-text-primary flex items-center space-x-2">
          <Icon name="Calendar" size={20} />
          <span>Today's Logs</span>
        </h2>
        <div className="text-right">
          <p className="text-sm text-text-secondary">Total time</p>
          <p className="text-xl sm:text-2xl font-bold text-primary">
            {formatHoursMinutes(summary?.totalHours || 0)}
          </p>
        </div>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="p-4 bg-success-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Play" size={16} color="var(--color-success)" />
            <p className="text-sm text-success-700 font-medium">First Check-in</p>
          </div>
          <p className="text-lg font-semibold text-success-800">
            {summary?.firstCheckIn ? formatTime(summary?.firstCheckIn) : '--:--'}
          </p>
        </div>

        <div className="p-4 bg-error-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Square" size={16} color="var(--color-error)" />
            <p className="text-sm text-error-700 font-medium">Last Check-out</p>
          </div>
          <p className="text-lg font-semibold text-error-800">
            {summary?.lastCheckOut
              ? formatTime(summary?.lastCheckOut)
              : summary?.isActive
                ? 'ACTIVE'
                : '--:--'}
          </p>
        </div>

        <div className="p-4 bg-primary-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Clock" size={16} color="var(--color-primary)" />
            <p className="text-sm text-primary-700 font-medium">Working time</p>
          </div>
          <p className="text-lg font-semibold text-primary-800">
            {formatHoursMinutes(summary?.totalHours || 0)}
          </p>
        </div>
      </div>
      {/* Logs Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="bg-secondary-50 px-4 py-3">
          <h3 className="text-sm font-medium text-text-primary">Time Entries</h3>
        </div>

        <div className="divide-y divide-border max-h-80">
          {!todayLogs?.data || todayLogs?.data?.length === 0 ? (
            <div className="p-6 sm:p-8 text-center">
              <Icon
                name="Clock"
                size={32}
                color="var(--color-text-secondary)"
                className="mx-auto mb-3"
              />
              <p className="text-text-secondary">No time entries for today</p>
              <p className="text-sm text-text-secondary mt-1">
                Click the check-in button to start tracking your time
              </p>
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="bg-secondary-100 px-4 py-3 border-b border-border">
                <div className="grid grid-cols-[10%_23%_23%_20%_24%] gap-2 sm:gap-4 items-center">
                  <div className="text-xs font-semibold text-text-primary uppercase tracking-wide">
                    #
                  </div>
                  <div className="text-xs font-semibold text-text-primary uppercase tracking-wide">
                    IN
                  </div>
                  <div className="text-xs font-semibold text-text-primary uppercase tracking-wide">
                    OUT
                  </div>
                  <div className="text-xs font-semibold text-text-primary uppercase tracking-wide">
                    TIME
                  </div>
                  <div className="text-xs font-semibold text-text-primary uppercase tracking-wide">
                    REASON
                  </div>
                </div>
              </div>

              {/* Table Rows */}
              {todayLogs?.data?.map((entry, index) => {
                // Calculate session time
                const sessionTime = entry?.check_out
                  ? calculateWorkingHours(entry?.check_in, entry?.check_out)
                  : calculateWorkingHours(entry?.check_in, getCurrentDateTime());

                // Format session time based on whether it's active or completed
                const sessionTimeDisplay = formatTimeHHMMSS(sessionTime);
                // ? formatHoursMinutes(sessionTime)
                // : formatTimeHHMMSS(sessionTime);
                return (
                  <div
                    key={index}
                    className="p-4 hover:bg-secondary-50 transition-colors duration-200"
                  >
                    <div className="grid grid-cols-[10%_23%_23%_20%_24%] gap-2 sm:gap-4 items-center">
                      <div className="text-sm font-medium text-text-primary">{index + 1}</div>

                      <div className="text-sm text-text-primary">{formatTime(entry?.check_in)}</div>

                      <div
                        className={`text-sm font-medium ${
                          entry?.check_out ? 'text-text-primary' : 'text-warning font-bold'
                        }`}
                      >
                        {entry?.check_out ? formatTime(entry?.check_out) : 'ACTIVE'}
                      </div>

                      <div
                        className={`text-sm font-medium ${
                          entry?.check_out ? 'text-text-primary' : 'text-success font-bold'
                        }`}
                      >
                        {sessionTimeDisplay}
                      </div>

                      <div className="text-sm text-text-primary">
                        {entry?.reason || (entry?.check_out ? '--' : 'Working')}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TodayLogsWidget;
